/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import { useLocation } from '@reach/router';
import { graphql, Script, useStaticQuery } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import React from 'react';
import ogImageCN from 'src/assets/perpetua-io-ogimage-CN.png';
import ogImageDE from 'src/assets/perpetua-io-ogimage-DE.png';
import ogImageEN from 'src/assets/perpetua-io-ogimage-EN.png';
import ogImageES from 'src/assets/perpetua-io-ogimage-ES.png';
import ogImageJA from 'src/assets/perpetua-io-ogimage-JA.png';
import { PageContext } from '../../types/pageContext';
import { chinaSiteURL } from '_utils/localization';
interface Props {
  description?: string;
  lang?: string;
  meta?: JSX.IntrinsicElements['meta'][];
  link?: JSX.IntrinsicElements['link'][];
  title: string;
  image?: string;
  schema?: any;
  canonical?: any;
  robots?: any;
  pageContext?: PageContext;
}

const SEO: React.FC<Props> = props => {
  const {
    description,
    meta = [],
    link = [],
    title,
    image,
    schema,
    robots,
    canonical = null,
    pageContext,
  } = props;

  const data = useStaticQuery(
    graphql`
      query {
        site: site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  );
  const openGraphImageObj = {
    de: ogImageDE,
    ja: ogImageJA,
    es: ogImageES,
    en: ogImageEN,
    'zh-CN': ogImageCN,
    'en-US': ogImageEN,
  };
  const { language: _language, languages, originalPath } = useI18next();
  const language = pageContext?.language || _language;
  const metaDescription = description || data.site.siteMetadata.description;
  const defaultTitle = title || data.site.siteMetadata.title;
  const titleTemplate =
    language == 'ja' && !defaultTitle.includes('Perpetua(パーペチュア)')
      ? `${defaultTitle} - Perpetua(パーペチュア)`
      : defaultTitle.includes('| Perpetua')
      ? defaultTitle
      : `${defaultTitle} | Perpetua`;
  const ogImage = image
    ? image
    : `https://perpetua.io${openGraphImageObj[language]}`;
  const location = useLocation();
  const canonicalUrl =
    canonical !== null
      ? canonical
      : data.site.siteMetadata.siteUrl + location.pathname;
  const languageLinks = schema
    ? schema?.languagesAvailable?.map(({ language, url }) => ({
          href: language === 'zh-CN' ? chinaSiteURL + url :
            data.site.siteMetadata.siteUrl +
            `${language !== 'en-US' ? '/' + language : ''}` +
            url,
          hreflang: language !== 'en-US' ? language : 'en-US',
          rel: 'alternate',
        }))
    : languages.map(lang => {
        if (language !== lang) {
          console.log('lang', lang);
          return {
            href: lang === 'zh-CN' ? chinaSiteURL + originalPath :
              data.site.siteMetadata.siteUrl +
              `${lang !== 'en-US' ? '/' + lang : ''}` +
              originalPath,
            hreflang: lang !== 'en-US' ? lang : 'en-US',
            rel: 'alternate',
          };
        } else {
          return {};
        }
      });

  const robtsMeta =
    robots && robots !== null && robots != undefined ? robots : 'index, follow';

  schema?.languagesAvailable ? delete schema?.languagesAvailable : schema;

  const lang = language !== 'en-US' ? language : 'en';

  if(languageLinks && languageLinks.length > 1) {
    languageLinks.push({ href: languageLinks[0]?.href, rel: 'alternate', hreflang: 'x-default' });
  }

  return (
    <>
     <html lang={lang} />
      <title>{titleTemplate || title}</title>
      {schema && (
        <Script type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </Script>
      )}
      <link rel="canonical" href={canonicalUrl} />
      {languageLinks.map(({ href, rel, hreflang }, index) => (
        <link
          key={`${hreflang}-${index}`}
          href={href}
          rel={rel}
          hrefLang={hreflang}
        />
      ))}
      {link.length > 0 &&
        link.map(({ href, ...rest }, index) => (
          <link key={`${href}-${index}`} {...rest} />
        ))}
      {[
        {
          name: `robots`,
          content: robtsMeta,
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `title`,
          property: `og:title`,
          content: titleTemplate,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          name: `image`,
          property: `og:image`,
          content: ogImage,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: data.site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: titleTemplate,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: ogImage,
        },
        {
          name: 'google-site-verification',
          content: process.env.GATSBY_GOOGLE_TAG_MANAGER,
        },
        {
          name: 'google-site-verification',
          content: process.env.GATSBY_GOOGLE_SEARCH_CONSOLE,
        },
      ].map(({ name, content, ...props }, index) => (
        <React.Fragment key={`${name}-${index}`}>
          <meta
            name={name}
            key={`${name}-${index}`}
            content={content}
            {...props}
          />
        </React.Fragment>
      ))}
      {meta.length > 0 &&
        meta.map(({ name, ...rest }) => (
          <React.Fragment key={name}>
            <meta name={name} key={name} {...rest} />
          </React.Fragment>
        ))}
    </>
  );
};

export default SEO;
